import { Show, SimpleShowLayout, TextField, DateField, NumberField, useNotify, ImageField, BooleanField, useShowController, useRedirect, useRefresh, useGetIdentity } from 'react-admin';
import { Box, Typography, Card, CardContent, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField as MuiTextField, CircularProgress, Alert, Link as MuiLink } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useState, useEffect, useMemo } from 'react';
import { customReasonsProxy, reviewsProxy } from './apiGateway';
import { DEFAULT_REVIEW_REASONS, ReasonGroup, ReasonType } from './reasonsConfig';
import { canOpenPlatformUserShow, getPlatformUserShowUrl } from './platformUserShowLink';

type CustomReason = {
    id: string;
    label: string;
    value: string;
    group: ReasonGroup;
    reasonType: ReasonType;
};

export const ReviewShow = () => {
    const { record, isLoading, error } = useShowController();
    const notify = useNotify();
    const redirect = useRedirect();
    const refresh = useRefresh();
    const { identity } = useGetIdentity();
    const currentUser = (identity ?? null) as { role?: string } | null;
    const canOpenUserShow = canOpenPlatformUserShow(currentUser?.role);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [refuseReason, setRefuseReason] = useState('');
    const [customReviewReasons, setCustomReviewReasons] = useState<CustomReason[]>([]);
    const [reasonsLoading, setReasonsLoading] = useState(false);

    useEffect(() => {
        let isActive = true;

        const loadReasons = async () => {
            setReasonsLoading(true);
            try {
                const result = await customReasonsProxy.getList();
                const reasons = ((result?.data as CustomReason[]) || [])
                    .filter((reason) => reason.group === 'reviews')
                    .map((reason) => ({
                        ...reason,
                        reasonType: reason.reasonType ?? 'refusal'
                    }));
                if (isActive) {
                    setCustomReviewReasons(reasons);
                }
            } catch (error) {
                console.error('Failed to load custom reasons', error);
                notify('Не удалось загрузить причины', { type: 'warning' });
            } finally {
                if (isActive) {
                    setReasonsLoading(false);
                }
            }
        };

        loadReasons();

        return () => {
            isActive = false;
        };
    }, [notify]);

    const reviewReasons = useMemo(
        () => [
            ...DEFAULT_REVIEW_REASONS,
            ...customReviewReasons.map((reason) => ({
                label: reason.label,
                value: reason.value,
                reasonType: reason.reasonType
            }))
        ].filter((reason) => reason.reasonType === 'refusal'),
        [customReviewReasons]
    );

    const handlePublish = async () => {
        try {
            await reviewsProxy.updateStatus(record.id, {
                moderationResult: "APPROVED"
            });

            notify('Отзыв успешно опубликован', { type: 'success' });
            redirect('list', 'reviews');
            refresh();
        } catch (error) {
            notify('Ошибка при публикации отзыва', { type: 'error' });
        }
    };

    const handleRefuse = async () => {
        if (!refuseReason.trim()) {
            notify('Пожалуйста, укажите причину отказа', { type: 'warning' });
            return;
        }

        try {
            await reviewsProxy.updateStatus(record.id, {
                moderationResult: "REFUSED",
                reason: refuseReason.trim()
            });

            notify('Отзыв отклонен', { type: 'success' });
            setIsModalOpen(false);
            setRefuseReason('');
            redirect('list', 'reviews');
            refresh();
        } catch (error) {
            notify('Ошибка при отказе в публикации', { type: 'error' });
        }
    };

    if (isLoading) return <div>Загрузка...</div>;
    if (error) {
        notify('Ошибка при загрузке данных', { type: 'error' });
        return <div>Ошибка при загрузке данных</div>;
    }
    if (!record) return <div>Данные не найдены</div>;

    return (
        <Show title={`Модерация: отзыв ${record.id}`}>
            <SimpleShowLayout>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Содержание отзыва</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="subtitle1" gutterBottom>Дата последнего изменения/публикации</Typography>
                                <DateField source="date" showTime />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" gutterBottom>Оценка пользователя</Typography>
                                <NumberField source="content.reviewGrade" />
                            </Box>
                            {record.content.text && (
                                <Box>
                                    <Typography variant="subtitle1" gutterBottom>Текст отзыва</Typography>
                                    <TextField source="content.text" />
                                </Box>
                            )}
                            {record.content.photosUrl && record.content.photosUrl.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle1" gutterBottom>Фотографии</Typography>
                                    <Box sx={{ 
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: '1fr',
                                            sm: 'repeat(2, 1fr)',
                                            md: 'repeat(3, 1fr)',
                                            lg: 'repeat(4, 1fr)'
                                        },
                                        gap: 2
                                    }}>
                                        {record.content.photosUrl.map((url: string, index: number) => (
                                            <Card 
                                                key={index}
                                                onClick={() => window.open(url, '_blank')}
                                                sx={{ 
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        opacity: 0.8
                                                    },
                                                    aspectRatio: '1',
                                                    width: '100%'
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={url}
                                                    alt={`Фото ${index + 1}`}
                                                    sx={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                            </Card>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </CardContent>
                </Card>

                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Информация о пользователях</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                            <Box>
                                <Typography variant="subtitle1" gutterBottom>От кого отзыв</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Имя пользователя</Typography>
                                        <TextField source="users.from.name" />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Идентификатор пользователя</Typography>
                                        <Typography variant="body1">
                                            {record.users?.from?.id ? (
                                                canOpenUserShow ? (
                                                    <MuiLink
                                                        href={getPlatformUserShowUrl(record.users.from.id)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        underline="hover"
                                                        sx={{ fontSize: 'inherit' }}
                                                    >
                                                        {record.users.from.id}
                                                    </MuiLink>
                                                ) : record.users.from.id
                                            ) : '-'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Активный аккаунт</Typography>
                                        <BooleanField 
                                            source="users.from.isActive"
                                            valueLabelTrue="Да"
                                            valueLabelFalse="Нет"
                                        />
                                    </Box>
                                    {/* Аватар пользователя (от кого) */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                                        <Typography variant="body2" color="text.secondary">Аватар пользователя</Typography>
                                        {record.users.from.avatarUrl ? (
                                            <ImageField 
                                                source="users.from.avatarUrl" 
                                                sx={{
                                                    '& .RaImageField-image': {
                                                        borderRadius: '50%',
                                                        width: 50,
                                                        height: 50,
                                                        objectFit: 'cover',
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <Typography variant="body1">Отсутствует</Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" gutterBottom>На кого отзыв</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Имя пользователя</Typography>
                                        <TextField source="users.to.name" />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Идентификатор пользователя</Typography>
                                        <Typography variant="body1">
                                            {record.users?.to?.id ? (
                                                canOpenUserShow ? (
                                                    <MuiLink
                                                        href={getPlatformUserShowUrl(record.users.to.id)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        underline="hover"
                                                        sx={{ fontSize: 'inherit' }}
                                                    >
                                                        {record.users.to.id}
                                                    </MuiLink>
                                                ) : record.users.to.id
                                            ) : '-'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Активный аккаунт</Typography>
                                        <BooleanField 
                                            source="users.to.isActive"
                                            valueLabelTrue="Да"
                                            valueLabelFalse="Нет"
                                        />
                                    </Box>
                                    {/* Аватар пользователя (на кого) */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                                        <Typography variant="body2" color="text.secondary">Аватар пользователя</Typography>
                                        {record.users.to.avatarUrl ? (
                                            <ImageField 
                                                source="users.to.avatarUrl" 
                                                sx={{
                                                    '& .RaImageField-image': {
                                                        borderRadius: '50%',
                                                        width: 50,
                                                        height: 50,
                                                        objectFit: 'cover',
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <Typography variant="body1">Отсутствует</Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Информация о сделке</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Номер заказа</Typography>
                                <TextField source="deal.orderId" />
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Айтем</Typography>
                                <Typography>
                                    {`${record.deal.brand} · ${record.deal.model}`}
                                </Typography>
                            </Box>
                            {record.deal.cancelled && (
                                <>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Сделка состоялась</Typography>
                                        <BooleanField 
                                            source="deal.cancelled.wasBought" 
                                            valueLabelTrue="Да"
                                            valueLabelFalse="Нет"
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Результат сделки</Typography>
                                        <Typography>
                                            {(() => {
                                                switch (record.deal.cancelled.dealResult) {
                                                    case 'MEETING':
                                                        return 'Была встреча с продавцом';
                                                    case 'ITEM_SENT':
                                                        return 'Вещь была отправлена';
                                                    case 'CHAT_WITH_SELLER':
                                                        return 'Было общение с продавцом';
                                                    case 'NO_COMMUNICATION':
                                                        return 'Общения с продавцом не было';
                                                    default:
                                                        return record.deal.cancelled.dealResult;
                                                }
                                            })()}
                                        </Typography>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </SimpleShowLayout>
            <Box sx={{ 
                mt: 4, 
                mb: 2, 
                display: 'flex', 
                justifyContent: 'center',
                '& .MuiButton-root': {
                    minWidth: '200px',
                    py: 1.5
                }
            }}>
                <Stack direction="row" spacing={2}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => redirect('list', 'reviews')}
                    >
                        Назад
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon />}
                        onClick={handlePublish}
                    >
                        Опубликовать
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => setIsModalOpen(true)}
                    >
                        Не публиковать
                    </Button>
                </Stack>
            </Box>

            <Dialog 
                open={isModalOpen} 
                onClose={() => {
                    setIsModalOpen(false);
                    setRefuseReason('');
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Причина отказа в публикации</DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                            Шаблоны отказов:
                        </Typography>
                        <Stack 
                            direction="row" 
                            sx={{ 
                                flexWrap: 'wrap', 
                                gap: 2,
                                '& .MuiButton-root': {
                                    px: 2,
                                    py: 1
                                }
                            }}
                        >
                            {reasonsLoading ? (
                                <CircularProgress size={24} />
                            ) : reviewReasons.length === 0 ? (
                                <Alert severity="info">Нет причин отказа.</Alert>
                            ) : (
                                reviewReasons.map((reason) => (
                                    <Button
                                        key={`${reason.label}-${reason.value}`}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setRefuseReason(reason.value)}
                                    >
                                        {reason.label}
                                    </Button>
                                ))
                            )}
                        </Stack>
                    </Box>
                    <MuiTextField
                        autoFocus
                        margin="dense"
                        label="Причина отказа"
                        fullWidth
                        multiline
                        rows={4}
                        value={refuseReason}
                        onChange={(e) => setRefuseReason(e.target.value)}
                        InputProps={{
                            readOnly: true,
                        }}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 2 }}>
                    <Button 
                        onClick={() => {
                            setIsModalOpen(false);
                            setRefuseReason('');
                        }}
                        sx={{ minWidth: '120px' }}
                    >
                        Отмена
                    </Button>
                    <Button 
                        onClick={handleRefuse}
                        color="error"
                        variant="contained"
                        sx={{ minWidth: '120px' }}
                    >
                        Не публиковать
                    </Button>
                </DialogActions>
            </Dialog>
        </Show>
    );
}; 
